import { groupBy, keyBy } from 'lodash';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { VirtualizedMenuList } from 'components/molecules/assignee-picker/virtualised-list';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useActiveOrInvitedNonCustomerUsersQuery } from 'queries/members';
import { fullName } from 'utils/full-name';
import { sortByText } from 'utils/sort-by-text';
import {
  UserEditorComponents,
  EditorComponents,
} from '../tasks/criteria/editor-components';

// Hardcoded font-size to match the size of other group headers
const Heading = styled.div`
  padding: 0 var(--spacing-04);
  text-transform: uppercase;
  font-size: 10.5px;
  color: var(--gray-50);
`;

export const UserGroupedByCompanyHeading = ({ children }) => {
  return <Heading>{children}</Heading>;
};

export const UserGroupedByCompanyMenuList = (props) => {
  const { children } = props;
  const {
    user: {
      company: { companyName: currentUserCompanyName },
    },
  } = useUser();
  const { data: users } = useActiveOrInvitedNonCustomerUsersQuery();
  const groupedByCompany = useMemo(
    () =>
      groupBy(
        (users ?? []).sort((a, b) => sortByText(fullName(a), fullName(b))),
        'company.companyName',
      ),
    [users],
  );
  const userOptionsDictionary = useMemo(
    () => keyBy(children, 'props.data.value'),
    [children],
  );
  const yourTeamText = useTranslation('your_team');

  const modifiedProps = useMemo(
    () => ({
      ...props,
      selectProps: {
        ...props.selectProps,
        grouped: true,
      },
      children: Object.entries(groupedByCompany)
        .sort(([companyNameA], [companyNameB]) => {
          if (companyNameA === currentUserCompanyName) {
            return -1;
          }
          if (companyNameB === currentUserCompanyName) {
            return 1;
          }

          return sortByText(companyNameA, companyNameB);
        })
        .map(([companyName, usersInCompany]) => {
          const label =
            companyName === currentUserCompanyName ? yourTeamText : companyName;

          return {
            props: {
              data: {
                label,
              },
              label,
              children: usersInCompany
                .map(({ userId }) => userOptionsDictionary[userId])
                .filter(Boolean),
            },
          };
        })
        .filter((option) => option.props.children.length > 0),
    }),
    [
      currentUserCompanyName,
      groupedByCompany,
      props,
      userOptionsDictionary,
      yourTeamText,
    ],
  );

  return <VirtualizedMenuList {...modifiedProps} />;
};

export const UsersGroupedByCompanyComponents: EditorComponents = {
  ...UserEditorComponents,
  GroupHeading: UserGroupedByCompanyHeading,
  MenuList: UserGroupedByCompanyMenuList,
};
